import { useEffect, useRef, useState } from 'react'
import CloseIconButton, { DARK_APPEARANCE } from '../../../components/CloseIconButton'

// The row's 18px box (01.10 export #start-analysis-pop-up, codex:check): checked #F3FFEB / #4CBC18 with
// the drawn tick, unchecked white / #C6CFC6, which goes #4CBC18 on hover as the other checkboxes do
// (letter 104, 1.1.1).
function StartCheckbox({ checked, hovered }) {
  const stroke = checked || hovered ? '#4CBC18' : '#C6CFC6'
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" className="shrink-0">
      <rect x="0.5" y="0.5" width="17" height="17" rx="1.5" fill={checked ? '#F3FFEB' : 'white'} stroke={stroke} />
      {checked && <path d="M5.25 9L7.9 11.9L12.75 6.75" stroke="#4CBC18" strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  )
}

// The whole row is the control and its name takes #328C12 on hover (mr-6 S4/S6: "Make entire name row
// clickable", "Add hover state color to name: #328C12").
function StartRow({ template, checked, onToggle, disabled }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={template.template_name}
      className="w-full h-[64px] flex items-center gap-x-[8px] border-b border-brand-fieldBdr text-left disabled:cursor-default"
    >
      <StartCheckbox checked={checked} hovered={hovered} />
      <span className={`flex-1 min-w-0 truncate text-[14px] ${hovered ? 'text-brand-hover' : 'text-brand-dark'}`}>
        {template.template_name}
      </span>
    </button>
  )
}

export const startLabel = (count) => `Start with ${count} ${count === 1 ? 'scenario' : 'scenarios'}`

// "Start your analysis" (letter 106 D1, D2; 01.10 export #start-analysis-pop-up): opens with no scenario
// on the property, one scenario per checked template. 486 wide, 48 top and bottom, 40 sides, baselines
// 67 and 91, rows 64 from 123 down, the button 48 under them. README.md, "Start your analysis".
export default function StartAnalysisModal({ templates, checkedIds, onToggleChecked, onStart, onClose, busy, error }) {
  const backdropRef = useRef(null)
  const listRef = useRef(null)
  const count = checkedIds.size
  // Focus starts in the dialog, on the first row.
  useEffect(() => {
    listRef.current?.querySelector('[role="checkbox"]')?.focus({ preventScroll: true })
  }, [])
  // Escape closes it like the X; `onClose` ignores it while the request runs.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])
  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center"
      // mousedown + target identity, as the other template pop-ups.
      onMouseDown={(e) => {
        if (e.target === backdropRef.current) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-analysis-title"
        aria-describedby="start-analysis-subtitle"
        className="relative w-[486px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] rounded-[16px] bg-[#F4FAEF] flex flex-col"
      >
        <div className="px-[16px] sm:px-[40px] pt-[48px]">
          <h2 id="start-analysis-title" className="text-[20px] leading-[24px] font-extrabold text-brand-dark">
            Start your analysis
          </h2>
          <p id="start-analysis-subtitle" className="mt-[2px] text-[14px] leading-[24px] text-brand-dark">
            Select one or more starting templates.
          </p>
          {/* The 16px glyph 20 from the top and right edges, as Manage Templates' X. */}
          <CloseIconButton
            onClick={onClose}
            ariaLabel="Close"
            appearance={DARK_APPEARANCE}
            span={16}
            stroke={1}
            className="absolute top-[16px] right-[16px]"
          />
        </div>

        <div ref={listRef} className="px-[16px] sm:px-[40px] mt-[25px] overflow-y-auto flex-1">
          {templates.length === 0 ? (
            // Not drawn: the server seeds Cash and Hard Money (D12), so only a failed seed lands here.
            <p className="py-[24px] text-[13px] text-brand-unselectedGray text-center">No templates yet. Reload the page to try again.</p>
          ) : (
            templates.map((template) => (
              <StartRow
                key={template._id}
                template={template}
                checked={checkedIds.has(template._id)}
                onToggle={() => onToggleChecked(template._id)}
                disabled={busy}
              />
            ))
          )}
        </div>

        <div className="px-[16px] sm:px-[40px] pt-[48px] pb-[48px] flex flex-col items-end">
          {error && (
            <p id="start-analysis-error" role="alert" className="mb-[8px] text-right text-[13px] text-brand-statusSale">
              {error}
            </p>
          )}
          {/* 40 tall, rx 20, 24 each side of the 13/600 label (177 wide for "Start with 2 scenarios");
              #A9B9A3 with nothing checked, as Update Template. */}
          <button
            type="button"
            onClick={onStart}
            disabled={count === 0 || busy}
            aria-describedby={error ? 'start-analysis-error' : undefined}
            className="h-[40px] rounded-[20px] px-[24px] bg-[#4AA52A] enabled:hover:bg-[#328C12] disabled:bg-[#A9B9A3] disabled:cursor-not-allowed text-white text-[13px] font-semibold whitespace-nowrap"
          >
            {startLabel(count)}
          </button>
        </div>
      </div>
    </div>
  )
}
